// CLI — `gridctl wire hook <url> [--event <event>]`.
//
// Subscribes a webhook URL to events on the entity bound to the current folder
// (cwd). Per Decision 042 Mode 1 (§wire), `wire hook` is the folder-rooted face
// of the same hook plumbing as `gridctl hooks add`: both post to the legacy
// `/hooks` API. The canonical daily-flow UX is `wire hook` — it targets the cwd
// entity automatically, so there is no `--app` to remember. `hooks add` stays
// for explicit app-scoped / platform-level subscriptions.
//
// Server contract (legacy client): POST /hooks { trigger, url, app }. The cwd
// entity's slug is the `app` scope; `--event` maps to `trigger`.

import { Command } from 'commander';
import { requireLink, type LinkArtifact } from '../services/link.js';
import { createApiClient, configExists } from '../config.js';
import { notSignedInMessage } from '../active-org.js';
import { CloudGridError } from '../utils.js';

/** Default trigger when `--event` is omitted: every event. Matches the
 *  wildcard the hooks pipeline already understands. */
const DEFAULT_EVENT = '*';

/** Minimal shape of the legacy client method `wire hook` calls. Lets tests
 *  inject a fake without standing up the whole legacy client. */
export interface HookClient {
  addHook(data: {
    trigger: string;
    url: string;
    app: string | null;
  }): Promise<{ hook?: { id?: string } }>;
}

export interface WireHookOpts {
  event?: string;
}

export interface WireHookDeps {
  client: HookClient;
  link: LinkArtifact;
}

/**
 * Testable core for `wire hook`: subscribe `url` to `--event` (default all)
 * on the cwd entity. Throws `CloudGridError` on a missing URL; surfaces the
 * created hook id on success.
 */
export async function runWireHook(
  url: string | undefined,
  opts: WireHookOpts,
  deps: WireHookDeps,
): Promise<void> {
  const target = (url ?? '').trim();
  if (target.length === 0) {
    throw new CloudGridError(
      'Provide a webhook URL. Usage: gridctl wire hook <url> [--event <event>]',
    );
  }
  const event = (opts.event ?? '').trim() || DEFAULT_EVENT;
  // The cwd entity's slug is the legacy `/hooks` `app` scope; fall back to the
  // entity id for links written before `entity_slug` landed.
  const app = deps.link.entity_slug ?? deps.link.entity_id;

  const result = await deps.client.addHook({ trigger: event, url: target, app });

  const scope = event === DEFAULT_EVENT ? 'all events' : `'${event}' events`;
  const lines = [`Subscribed ${target} to ${scope} on ${app}.`];
  const id = result?.hook?.id;
  if (id) lines.push(`Hook id: ${id}`);
  process.stdout.write(lines.join('\n') + '\n');
}

export function makeWireHookCommand(): Command {
  return new Command('hook')
    .description("Subscribe a webhook URL to events on this folder's entity")
    .argument('[url]', 'Webhook URL to call when events fire')
    .option('--event <event>', 'Event to subscribe to (defaults to all events)')
    .action(async (url: string | undefined, opts: WireHookOpts) => {
      try {
        if (!configExists()) throw new CloudGridError(notSignedInMessage());
        const link = requireLink();
        const client = createApiClient() as unknown as HookClient;
        await runWireHook(url, opts, { client, link });
      } catch (err) {
        // The legacy client throws plain Errors; normalise to CloudGridError
        // so the top-level handler prints the consistent "Error: ..." line.
        if (err instanceof CloudGridError) throw err;
        throw new CloudGridError(err instanceof Error ? err.message : String(err));
      }
    });
}
